import type { NearbySpot } from '../components/NearbyMap';

/**
 * Convert OSM POI data to card payload format
 * Maps NearbySpot data from OpenStreetMap to the appropriate card type structure
 */
export function createPayloadFromOSM(spot: NearbySpot): any {
  const category = spot.category || 'poi';
  const tags = (spot as any).tags || {} as Record<string, any>;

  // DEBUG: log what we received
  console.log('[osmToPayload] Input spot:', {
    name: spot.name,
    spotPhone: spot.phone,
    spotWebsite: spot.website,
    spotTags: spot.tags,
    tagsType: typeof spot.tags,
    tagsIsObject: typeof spot.tags === 'object' && spot.tags !== null,
    tagsKeys: tags ? Object.keys(tags) : [],
    tagsPhone: tags.phone,
    tagsWebsite: tags.website,
    tagsOpeningHours: tags.opening_hours,
  });

  // Extract critical fields from tags first
  const extractedPhone = spot.phone || tags.phone || tags['contact:phone'] || tags['contact:tel'] || tags.tel || null;
  const extractedWebsite = spot.website || tags.website || tags['contact:website'] || tags.url || null;
  const extractedOpeningHours = spot.opening_hours || tags.opening_hours || null;
  
  console.log('[osmToPayload] Extracted:', { extractedPhone, extractedWebsite, extractedOpeningHours });

  // Extract administrative location from addr:* tags
  const extractedCity = tags['addr:city'] || tags['addr:municipality'] || null;
  const extractedProvince = tags['addr:province'] || tags['addr:state'] || tags['addr:region'] || null;
  const extractedDistrict = tags['addr:district'] || tags['addr:suburb'] || tags['addr:neighbourhood'] || tags['addr:quarter'] || tags['addr:block_number'] || null;
  const extractedCountry = tags['addr:country'] || null;

  // Build metaPairs ONLY from available OSM data (no empty placeholders)
  const metaPairs: Array<{ label: string; value: string }> = [];
  const addMeta = (label: string, value?: any) => {
    if (value === undefined || value === null) return;
    const v = String(value).trim();
    if (!v) return;
    metaPairs.push({ label, value: v });
  };
  
  // Add fields in priority order (what we have from OSM)
  if (extractedPhone) addMeta('电话', extractedPhone);
  if (extractedWebsite) addMeta('网站', extractedWebsite);
  if (extractedOpeningHours) addMeta('营业时间', extractedOpeningHours);
  if (tags.brand) addMeta('品牌', tags.brand);
  if (tags.cuisine) addMeta('菜系', tags.cuisine);
  if (tags.diet) addMeta('特殊饮食', tags.diet);
  if (tags.takeaway) addMeta('外卖', tags.takeaway === 'yes' ? '支持' : '不支持');
  if (tags.delivery) addMeta('配送', tags.delivery === 'yes' ? '支持' : '不支持');
  if (tags.outdoor_seating) addMeta('户外座位', tags.outdoor_seating === 'yes' ? '有' : '无');
  if (tags.internet_access) addMeta('网络', tags.internet_access);
  if (tags.wheelchair) addMeta('无障碍', tags.wheelchair === 'yes' ? '支持' : '不支持');
  if (tags.smoking) addMeta('吸烟', tags.smoking === 'yes' ? '允许' : '不允许');
  if (tags.capacity) addMeta('容量', tags.capacity);
  if (tags.stars) addMeta('星级', tags.stars);
  if (tags.rooms) addMeta('房间数', tags.rooms);
  if (tags.fee) addMeta('收费', tags.fee === 'yes' ? '收费' : '免费');
  if (tags.charge) addMeta('费用', tags.charge);
  if (tags.note) addMeta('备注', tags.note);
  if (tags.description) addMeta('说明', tags.description);
  
  // Add type info
  const poiType = tags.amenity || tags.shop || tags.tourism || tags.leisure || category;
  if (poiType) addMeta('类型', poiType);
  
  // Knowledge links
  if (tags.wikidata) addMeta('Wikidata', `https://www.wikidata.org/wiki/${tags.wikidata}`);
  if (tags.wikipedia) {
    const wp = String(tags.wikipedia);
    const parts = wp.includes(':') ? wp.split(':') : ['en', wp];
    addMeta('Wikipedia', `https://${parts[0]}.wikipedia.org/wiki/${encodeURIComponent(parts.slice(1).join(':'))}`);
  }
  
  console.log('[osmToPayload] Generated metaPairs:', metaPairs);
  
  // Build clean location object with ONLY administrative fields
  const locationObj: any = {};
  if (extractedCity) locationObj.city = extractedCity;
  if (extractedCountry) locationObj.country = extractedCountry;
  if (extractedDistrict) locationObj.district = extractedDistrict;
  if (extractedProvince) locationObj.province = extractedProvince;

  // Base payload structure common to all types
  const basePayload = {
    card_type: getCubeMeetCardType(category),
    type: getCubeMeetCardType(category),
    title: spot.name || tags.name || '未命名地点',
    address: spot.address || '',
    description: spot.description || '',
    // Prefer existing imageUrl; otherwise build static map snapshot as a visual header
    imageUrl: buildStaticMap(spot.lat, spot.lng),
    cover_image: buildStaticMap(spot.lat, spot.lng),
    // Store coordinates for map display
    latitude: spot.lat,
    longitude: spot.lng,
    lat: spot.lat,
    lng: spot.lng,
    // Location object: ONLY administrative fields (city/country/district/province)
    location: Object.keys(locationObj).length > 0 ? locationObj : undefined,
    tags: [category, 'osm'].concat(tags.brand ? [tags.brand] : []),
    // Store OSM metadata
    osmData: {
      id: spot.id,
      source: 'openstreetmap',
      category: category,
      rawTags: tags,
    },
    website: extractedWebsite || undefined,
    phone: extractedPhone || undefined,
    opening_hours: extractedOpeningHours || undefined,
    // Don't include empty card object - modal will treat this as a new card
  };

  // Return unified payload with dynamic metaPairs (based on actual OSM data only)
  const finalPayload = {
    ...basePayload,
    metaPairs, // Use the metaPairs we built from actual OSM data
  };
  
  // DEBUG: Confirm what we're returning
  console.log('[osmToPayload] Final payload:', {
    hasMetaPairs: !!finalPayload.metaPairs,
    metaPairsCount: finalPayload.metaPairs?.length || 0,
    metaPairsSample: finalPayload.metaPairs?.slice(0, 3),
    phone: finalPayload.phone,
    website: finalPayload.website,
  });
  
  return finalPayload;
}

/**
 * Map OSM category to CubeMeet card type
 */
function getCubeMeetCardType(category: string): string {
  switch (category) {
    case 'lodging':
      return 'lodging';
    case 'restaurant':
      return 'restaurant';
    case 'shopping':
      return 'shopping';
    case 'poi':
    default:
      return 'poi';
  }
}

function buildStaticMap(lat: number, lng: number): string {
  // Static map service is unreliable, use placeholder instead
  // Users can upload their own cover image in edit mode
  return '/assets/placeholders/card-header-default.jpg';
}
